import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, CheckSquare, Calendar, Palette, Mail, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';
import emailjs from '@emailjs/browser';

function Resources() {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [downloading, setDownloading] = useState(null);

  const resources = [
    {
      id: 1,
      icon: CheckSquare,
      title: {
        sq: 'Website Planning Checklist',
        en: 'Website Planning Checklist'
      },
      description: {
        sq: 'Checklist e plotë për planifikimin e një website-i të suksesshëm. Përfshin 50+ pika kontrollimi.',
        en: 'Complete checklist for planning a successful website. Includes 50+ checkpoints.'
      },
      type: 'PDF',
      size: '2.5 MB',
      downloads: 450,
      preview: '/resources/website-checklist-preview.jpg'
    },
    {
      id: 2,
      icon: FileText,
      title: {
        sq: 'SEO Optimization Guide',
        en: 'SEO Optimization Guide'
      },
      description: {
        sq: 'Guidë 30-faqesh për SEO. Strategji dhe taktika që funksionojnë në 2026.',
        en: '30-page SEO guide. Strategies and tactics that work in 2026.'
      },
      type: 'PDF',
      size: '5.2 MB',
      downloads: 680,
      preview: '/resources/seo-guide-preview.jpg'
    },
    {
      id: 3,
      icon: Calendar,
      title: {
        sq: 'Social Media Content Calendar',
        en: 'Social Media Content Calendar'
      },
      description: {
        sq: 'Template Excel për planifikimin e content-it në social media për 3 muaj.',
        en: 'Excel template for planning social media content for 3 months.'
      },
      type: 'XLSX',
      size: '1.2 MB',
      downloads: 520,
      preview: '/resources/calendar-preview.jpg'
    },
    {
      id: 4,
      icon: Palette,
      title: {
        sq: 'Brand Identity Template',
        en: 'Brand Identity Template'
      },
      description: {
        sq: 'Template Figma për krijimin e brand identity. Përfshin logo guidelines, color palettes, typography.',
        en: 'Figma template for creating brand identity. Includes logo guidelines, color palettes, typography.'
      },
      type: 'Figma',
      size: '8.5 MB',
      downloads: 340,
      preview: '/resources/brand-template-preview.jpg'
    },
    {
      id: 5,
      icon: FileText,
      title: {
        sq: 'Email Marketing Templates',
        en: 'Email Marketing Templates'
      },
      description: {
        sq: '10 template email të gatshme për përdorim. Welcome series, promotional, newsletter.',
        en: '10 ready-to-use email templates. Welcome series, promotional, newsletter.'
      },
      type: 'HTML',
      size: '3.8 MB',
      downloads: 420,
      preview: '/resources/email-templates-preview.jpg'
    },
    {
      id: 6,
      icon: CheckSquare,
      title: {
        sq: 'Website Launch Checklist',
        en: 'Website Launch Checklist'
      },
      description: {
        sq: 'Çfarë duhet kontrolluar para se të launch-oni website-in tuaj. 40+ pika kritike.',
        en: 'What to check before launching your website. 40+ critical points.'
      },
      type: 'PDF',
      size: '1.8 MB',
      downloads: 390,
      preview: '/resources/launch-checklist-preview.jpg'
    }
  ];

  const handleDownload = async (resource) => {
    if (!email || !name) {
      showToast(
        language === 'sq' ? 'Ju lutem plotësoni email dhe emrin' : 'Please fill email and name',
        'error'
      );
      return;
    }

    setDownloading(resource.id);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_RESOURCES_TEMPLATE_ID,
        {
          user_name: name,
          user_email: email,
          resource_name: resource.title[language],
          resource_type: resource.type
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      showToast(
        language === 'sq' 
          ? 'Download link u dërgua në email!' 
          : 'Download link sent to email!',
        'success'
      );

      // Simulate download
      setTimeout(() => {
        setDownloading(null);
      }, 1500);
    } catch (error) {
      showToast(
        language === 'sq' ? 'Gabim në dërgim' : 'Error sending',
        'error'
      );
      setDownloading(null);
    }
  };

  return (
    <>
      <SEO 
        title={language === 'sq' ? 'Resources - Burime Falas' : 'Resources - Free Downloads'}
        description={language === 'sq' ? 'Shkarko template, guidë, dhe checklist falas' : 'Download free templates, guides, and checklists'}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 sm:py-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <Download className="w-4 h-4" />
              {language === 'sq' ? 'Burime Falas' : 'Free Resources'}
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {language === 'sq' ? 'Shkarkoni Burime të Dobishme' : 'Download Useful Resources'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {language === 'sq' 
                ? 'Template, guidë, dhe checklist për të përmirësuar biznesin tuaj' 
                : 'Templates, guides, and checklists to improve your business'}
            </p>
          </motion.div>

          {/* Email Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-12 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start gap-3 mb-4">
              <Lock className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">
                  {language === 'sq' ? 'Shkarkoni Falas' : 'Download for Free'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sq' 
                    ? 'Plotësoni informacionin për të shkarkuar burimet. Email-i juaj nuk do të shpërndahet.' 
                    : 'Fill in your information to download resources. Your email won\'t be shared.'}
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === 'sq' ? 'Emri juaj' : 'Your name'}
                className="px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={language === 'sq' ? 'Email juaj' : 'Your email'}
                className="px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </motion.div>

          {/* Resources Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {resources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-500 transition-all group"
              >
                {/* Preview Image */}
                <div className="h-48 bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30 flex items-center justify-center">
                  <resource.icon className="w-20 h-20 text-yellow-600 dark:text-yellow-400" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {resource.title[language]}
                    </h3>
                    <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-xs px-2 py-1 rounded-full font-semibold">
                      {resource.type}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {resource.description[language]}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500 mb-4">
                    <span>{resource.size}</span>
                    <span>{resource.downloads} {language === 'sq' ? 'shkarkime' : 'downloads'}</span>
                  </div>

                  <button
                    onClick={() => handleDownload(resource)}
                    disabled={downloading === resource.id}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-4 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {downloading === resource.id ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <Download className="w-5 h-5" />
                        </motion.div>
                        {language === 'sq' ? 'Duke shkarkuar...' : 'Downloading...'}
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        {language === 'sq' ? 'Shkarko' : 'Download'}
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-3xl mx-auto bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-8 text-center shadow-2xl"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'sq' ? 'Dëshironi Më Shumë Burime?' : 'Want More Resources?'}
            </h2>
            <p className="text-white/90 mb-6">
              {language === 'sq' 
                ? 'Regjistrohuni në newsletter-in tonë për të marrë burime ekskluzive çdo javë.' 
                : 'Subscribe to our newsletter to receive exclusive resources every week.'}
            </p>
            <div className="flex gap-2 max-w-md mx-auto">
              <input
                type="email"
                placeholder={language === 'sq' ? 'Email juaj' : 'Your email'}
                className="flex-1 px-4 py-3 rounded-lg"
              />
              <button className="bg-white text-yellow-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {language === 'sq' ? 'Regjistrohu' : 'Subscribe'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Resources;